import { useCallback, useState } from 'react';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useGetBenefitsQuery, useLogEngagementMutation } from 'services/resourceService';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useBenefitModalRoute } from './useBenefitModalRoute';
import { BENEFITS_PAGE_SIZE } from '../utils/benefitsUtils';
import type { Benefit } from '../types';

export type { Benefit };
export { BENEFITS_PAGE_SIZE };

export interface UseBenefitsOptions {
  /** Benefits per page. Pass a small value (e.g. 4) for a preview; omit for the full paginated list. */
  pageSize?: number;
  /** Skips the request entirely, e.g. when the benefits feature flag is off. */
  skip?: boolean;
}

export interface UseBenefitsResult {
  benefits: Benefit[];
  hasMore: boolean;
  /** Appends the next page to `benefits`. Preview callers simply never call it. */
  loadNextPage: () => void;
  /** Re-requests the page that last failed. */
  retry: () => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** The benefit whose detail modal is open, driven by the `benefitId` URL param. */
  selectedBenefit: Benefit | null;
  isModalOpen: boolean;
  closeModal: () => void;
  /** Opens the full benefits page. */
  handleViewAll: () => void;
  /** Opens the benefit's detail modal. */
  handleBenefitCardClick: (benefit: Benefit) => void;
}

/**
 * Provides the member's benefit topics, one page at a time.
 * - `useBenefits({ pageSize: 4 })`: first 4 benefits (preview).
 * - `useBenefits()`: all benefits, `BENEFITS_PAGE_SIZE` per page via `loadNextPage`.
 */
export const useBenefits = ({
  pageSize = BENEFITS_PAGE_SIZE,
  skip = false,
}: UseBenefitsOptions = {}): UseBenefitsResult => {
  const user = useLoggedInUser();
  const memberUuid = user?.uuid ?? '';
  const { currentLocale } = useTranslationContext();
  const navigate = useNavigate();
  // The page is tracked per language: a language switch opens a fresh cache entry
  const [pageState, setPageState] = useState({ language: currentLocale, page: 1 });
  const page = pageState.language === currentLocale ? pageState.page : 1;

  const { data, isLoading, isFetching, isError, refetch } = useGetBenefitsQuery(
    { memberUuid, page, pageSize, language: currentLocale },
    { skip: skip || !memberUuid },
  );
  const [logEngagement] = useLogEngagementMutation();

  // Fire-and-forget: engagement logging must never disrupt the member experience.
  const logBenefitView = () => {
    if (!memberUuid) {
      return;
    }
    logEngagement({
      memberUuid,
      body: { url: null, engagement_type: 'MEMBER_BENEFITS_VIEW' },
    })
      .unwrap()
      .catch((err) => {
        console.error(err);
      });
  };

  const loadedPages = data?.lastLoadedPage ?? 0;
  const hasMore = data ? loadedPages < data.totalPages : false;

  const loadNextPage = useCallback(() => {
    // `page > loadedPages` means the next page is in flight or failed; failures go through `retry`.
    if (hasMore && !isFetching && page <= loadedPages) {
      setPageState({ language: currentLocale, page: loadedPages + 1 });
    }
  }, [hasMore, isFetching, page, loadedPages, currentLocale]);

  // The failed page is still the current query arg, so refetching requests it again.
  const retry = useCallback(() => {
    if (memberUuid && !skip && !isFetching) {
      refetch();
    }
  }, [memberUuid, skip, isFetching, refetch]);

  const benefits = data?.benefits ?? [];
  const { selectedBenefit, isModalOpen, openBenefit, closeModal } = useBenefitModalRoute(benefits);

  const handleViewAll = () => navigate('/resources/benefits');

  const handleBenefitCardClick = (benefit: Benefit) => {
    openBenefit(benefit);
    logBenefitView();
  };

  return {
    benefits,
    hasMore,
    loadNextPage,
    retry,
    isLoading,
    isFetching,
    isError,
    selectedBenefit,
    isModalOpen,
    closeModal,
    handleViewAll,
    handleBenefitCardClick,
  };
};
